/**
 * DATA EXPORT/IMPORT MODULE
 * Handles exporting all user data and importing from backups
 *
 * Export format:
 * {
 *   version: 2,
 *   exportedAt: ISO timestamp,
 *   folders: [...],  // Folder structure
 *   documents: [
 *     {
 *       id: docId,
 *       name: string,
 *       folderId: string,
 *       createdAt: timestamp,
 *       updatedAt: timestamp,
 *       crdtState: base64 string (Yjs document state),
 *       meta: { pinned, color }
 *     }
 *   ],
 *   // Note: wrappedDEKs are NOT exported - they're re-generated on import
 * }
 */

import { recordQuotaRejection } from '../services/quota-state.js';
import { isQuotaError } from '../sync/server-errors.js';
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { readMap, readTree } from "../sync/map-format.js";
import { mergeBackupState, remapBackupLinks, backupAttachmentReferences, serverHasLocalCopyEncrypted } from "./backup-state.js";
import { readMapArchive, remapAttachmentData } from '../../shared/utils/map-archive.js';
import { restoreMapAttachments, restoredAttachmentNote, attachArchiveContext } from '../services/map-archive-import.js';
import { listAttachments } from "../services/attachment-service.js";
import { runOperation } from "../host.js";
import { uploadWithProgress, readResponseWithProgress } from "../../shared/utils/transfer-progress.js";
import { t } from "../../shared/i18n/index.js";
import {
  ensureNotesSerializer,
  isNotesSerializerLoaded,
  fragmentToMarkdown,
} from "../sync/notes-serializer-facade.js";
import { changeMark, markConfirmedIfUnchanged } from "../sync/unsynced-changes.js";
import { readVaultFile, writeVaultFile } from "../storage/index.js";
// The vault, signed in or not (it was given by the vault panel when signed
// in only: copies and exports failed signed out).
import { VaultStorage } from "./vault-storage.js";

/**
 * Export all user data (maps) for backup/GDPR compliance
 *
 * @param {Object} options
 * @param {Function} options.onProgress - Progress callback (current, total, name)
 * @returns {Promise<Object>} Export data object
 */
export async function exportAllData(options = {}) {
  return runOperation(t('vault.export.prepareTitle'), progress => exportAllDataImpl({ ...options, onActivity: progress.update }),
    result => [t('vault.export.prepared'), result.exportErrors.length > 0]);
}

async function exportAllDataImpl(options) {
  const index = VaultStorage.getIndex();
  if (!index) throw new Error("Vault not loaded");
  const exportErrors = [];
  const userSync = await import('../sync/user-crdt-bridge.js');
  if (userSync.isOnlineEnabled() && userSync.getConnectionState() !== 'synced') {
    exportErrors.push({ name: t('vault.export.warn.indexName'), error: t('vault.export.warn.syncIncomplete') });
  }
  try { await ensureNotesSerializer(); }
  catch { exportErrors.push({ name: t('vault.export.warn.notesName'), error: t('vault.export.warn.notesFailed') }); }
  const exportData = {
    version: 2, exportedAt: new Date().toISOString(),
    folders: index.folders.map(f => ({ id: f.id, parentId: f.parentId, name: f.name, storage: f._storage || 'online',
      createdAt: f.createdAt, updatedAt: f.updatedAt })), documents: [],
    backup: { scope: 'documents-and-folders', attachmentsIncluded: false, warnings: [] },
  };
  const documents = (index.docs || []).filter(doc => !options.docIds || options.docIds.includes(doc.docId));
  if (options.docIds?.length && documents.length !== options.docIds.length) throw new Error(t('vault.export.mapUnavailable'));
  if (options.docIds) {
    // A single-map backup must not reveal unrelated vault folder names.
    const byId = new Map(exportData.folders.map(folder => [folder.id, folder]));
    const included = new Set();
    for (const doc of documents) {
      let folderId = doc.role && doc.role !== 'owner' ? null : doc.folderId;
      while (byId.has(folderId) && !included.has(folderId)) {
        included.add(folderId);
        folderId = byId.get(folderId).parentId;
      }
    }
    exportData.folders = exportData.folders.filter(folder => included.has(folder.id));
  }
  for (let i = 0; i < documents.length; i++) {
    const entry = documents[i];
    options.onProgress?.(i + 1, documents.length, entry.title);
    options.onActivity?.({ label: t('vault.export.collecting'), current: i, total: documents.length, detail: entry.title });
    const warnings = [];
    let local = null, server = null, key = null, state = null;
    const storage = entry._storage || 'online';
    let attachmentCount = null;
    try {
      const loaded = await loadLocalBackupState(entry.docId);
      local = loaded.state;
      warnings.push(...loaded.warnings);
      if (storage === 'online') {
        try {
          const rawKey = await VaultStorage.getDocDEK(entry.docId);
          try { key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['decrypt']); }
          finally { rawKey.fill(0); }
          const response = await fetch(`/api/docs/${entry.docId}/state`, { credentials: 'include' });
          if (!response.ok) throw new Error(`Server returned ${response.status}`);
          server = JSON.parse(new TextDecoder().decode(await readResponseWithProgress(response, event =>
            options.onActivity?.({ ...event, label: t('vault.export.downloading'), detail: entry.title }))));
        } catch (error) { warnings.push(t('vault.export.warn.serverUnverified', { error: error.message })); }
        try {
          attachmentCount = (await listAttachments(entry.docId)).length;
          if (attachmentCount && options.includeAttachments === undefined) warnings.push(t('vault.export.warn.attachmentsExcluded', { count: attachmentCount }));
        } catch { warnings.push(t('vault.export.warn.attachmentsUnchecked')); }
      }
      options.onActivity?.({ label: t('vault.export.merging'), current: i, total: documents.length, detail: entry.title });
      const merged = await mergeBackupState(local, server, key, entry.docId);
      state = merged.state;
      warnings.push(...merged.warnings);
      if (!state) warnings.push(t('vault.export.warn.noState'));
    } catch (error) {
      // Keep even a damaged local source in the bundle for later investigation.
      state = local;
      warnings.push(t('vault.export.warn.incomplete', { error: error.message }));
    }
    let readable = state ? crdtToReadableTree(state) : null;
    try { JSON.stringify(readable); }
    catch { readable = null; warnings.push(t('vault.export.warn.cycle')); }
    const shared = !!entry.role && entry.role !== 'owner';
    exportData.documents.push({
      id: entry.docId, name: shared ? t('vault.export.sharedName', { title: entry.title }) : entry.title,
      folderId: shared ? null : entry.folderId, createdAt: entry.createdAt || entry.addedAt,
      updatedAt: entry.updatedAt, content: readable?.tree || null, docNotes: readable?.docNotes || [], routines: readable?.routines || [],
      crdtState: state ? arrayBufferToBase64(state) : null, storage,
      meta: entry.meta || { pinned: false, color: null },
      ...(shared && { source: 'shared', ownerUsername: entry.ownerUsername || null, role: entry.role }),
      backup: { attachmentCount, warnings },
    });
    for (const error of warnings) exportErrors.push({ name: entry.title || entry.docId, error });
  }
  // Warnings travel with the file, not just the transient export dialog.
  exportData.backup.warnings = exportErrors;
  exportData.backup.completeDocumentExport = exportErrors.length === 0;
  return { exportData, exportErrors };
}

/**
 * Merge live work and persisted sources independently, retaining readable
 * data and warnings even when another source is unavailable.
 */
async function loadLocalBackupState(docId) {
  const sources = [], warnings = [];
  try {
    const bridge = await import('../sync/crdt-bridge.js');
    const shares = await import('../sync/share-lifecycle.js');
    const main = bridge.getDocManager();
    const managers = new Set([main?.docId === docId ? main : null,
      bridge.getLinkedDocManager(docId), shares.getSharedDocManager(docId)]);
    for (const manager of managers) {
      if (manager?.doc) sources.push(Y.encodeStateAsUpdate(manager.doc));
    }
  } catch (error) { warnings.push(t('vault.export.warn.liveUnreadable', { error: error.message })); }
  const native = await loadCRDTStateFromTauri(docId);
  sources.push(...native.sources);
  warnings.push(...native.warnings);
  try {
    const bytes = await loadCRDTStateFromIndexedDB(docId);
    if (bytes) sources.push(bytes);
  } catch (error) { warnings.push(t('vault.export.warn.indexedDbUnreadable', { error: error.message })); }
  const merged = await mergeBackupState(sources, null, null, docId);
  return { state: merged.state, warnings: [...warnings, ...merged.warnings] };
}

/**
 * Before signing out, for maps whose changes this device never tracked
 * (unsynced-changes.js; edited before it existed): compare each copy here
 * with the server's. Must run while still signed in (it reads the server's
 * copies).
 *
 * @param {string[]} docIds the account's online maps
 * @returns {Promise<{removable: string[], kept: string[], unchecked: string[]}>}
 *   removable: the server has all of it; kept: the server lacks changes made
 *   here; unchecked: could not be compared
 */
export async function checkOnlineCopiesBeforeSignOut(docIds, { onProgress } = {}) {
  const removable = []; // the server has everything this device has
  const kept = []; // the server lacks changes this device has
  const unchecked = []; // could not be compared
  for (let i = 0; i < docIds.length; i++) {
    const docId = docIds[i];
    onProgress?.({ label: t('account.signOut.checking'), current: i, total: docIds.length });
    try {
      const { state, warnings } = await loadLocalBackupState(docId);
      if (warnings.length) { unchecked.push(docId); continue; }
      if (!state) { removable.push(docId); continue; }
      const rawKey = await VaultStorage.getDocDEK(docId);
      let key;
      try { key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['decrypt']); }
      finally { rawKey.fill(0); }
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      let server;
      try {
        const response = await fetch(`/api/docs/${docId}/state`, { credentials: 'include', signal: controller.signal });
        if (!response.ok) { unchecked.push(docId); continue; }
        server = await response.json();
      } finally {
        clearTimeout(timeout);
      }
      (await serverHasLocalCopyEncrypted(state, server, key, docId) ? removable : kept).push(docId);
    } catch {
      unchecked.push(docId);
    }
  }
  onProgress?.({ label: t('account.signOut.checking'), current: docIds.length, total: docIds.length });
  return { removable, kept, unchecked };
}

/**
 * A map's full content as held on this device: the open copy, the desktop
 * file and browser storage, merged. `warnings` lists sources that could not be
 * read (callers moving a map must not proceed on a partial read).
 * @returns {Promise<{state: Uint8Array|null, warnings: string[]}>}
 */
export function readLocalMapState(docId) {
  return loadLocalBackupState(docId);
}

/**
 * An online map's complete content: the server's records merged with every
 * copy on this device (moving it to this device; the device file alone may
 * be stale). `warnings` lists sources that could not be read: a caller
 * moving the map must not proceed on a partial read. Throws when the
 * server's copy can't be fetched.
 * @param {string} docId
 * @param {Uint8Array} dekBytes the map's key
 * @returns {Promise<{state: Uint8Array|null, warnings: string[]}>}
 */
export async function readCompleteMapState(docId, dekBytes) {
  const local = await loadLocalBackupState(docId);
  const key = await crypto.subtle.importKey('raw', dekBytes, 'AES-GCM', false, ['decrypt']);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);
  let server;
  try {
    const response = await fetch(`/api/docs/${docId}/state`, { credentials: 'include', signal: controller.signal });
    if (!response.ok) throw new Error(t('app.mapStorage.toDeviceError.serverStatus', { status: response.status }));
    server = await response.json();
  } finally {
    clearTimeout(timeout);
  }
  const merged = await mergeBackupState([local.state], server, key, docId);
  return { state: merged.state, warnings: [...local.warnings, ...merged.warnings] };
}

/**
 * The backup of a map's copy on this device alone, when its online copy is
 * gone (moved to another device, or deleted there): changes made here and
 * never sent exist only in it. The app's export format, so the file can be
 * imported; attachments are not included (they were online). The interface
 * saves it (interfaces/classic/ui/export-flow.js exportLocalCopy).
 * @returns {Promise<{exportData: Object, filename: string}>}
 */
export async function buildLocalCopyExport(docId, title) {
  const { state, warnings } = await loadLocalBackupState(docId);
  if (!state) throw new Error(t('vault.export.warn.noState'));
  let readable = crdtToReadableTree(state);
  try { JSON.stringify(readable); } catch { readable = null; warnings.push(t('vault.export.warn.cycle')); }
  const errors = warnings.map((error) => ({ name: title || docId, error }));
  const exportData = {
    version: 2, exportedAt: new Date().toISOString(), folders: [],
    documents: [{
      id: docId, name: title, folderId: null, createdAt: null, updatedAt: Date.now(),
      content: readable?.tree || null, docNotes: readable?.docNotes || [], routines: readable?.routines || [],
      crdtState: arrayBufferToBase64(state), storage: 'local', meta: { pinned: false, color: null },
      backup: { attachmentCount: null, warnings },
    }],
    backup: { scope: 'documents-and-folders', attachmentsIncluded: false, warnings: errors, completeDocumentExport: errors.length === 0 },
  };
  const date = new Date().toISOString().split('T')[0];
  const name = (title || 'map').replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 80) || 'map';
  return { exportData, filename: `${name}-${date}.json` };
}

/**
 * Store a map's full content on the server as its baseline, encrypted with
 * its DEK (moving a map from this device online). Throws on failure.
 */
export async function uploadMapBaseline(docId, state, dekBytes) {
  const crdtState = await encryptCRDTForSync(state, dekBytes, docId);
  const result = await syncImportedDocumentsToServer([{ doc_id: docId, crdt_state: crdtState }]);
  if (!result.success) throw new Error(result.errors.join('; ') || 'upload failed');
}

/**
 * Load a document from IndexedDB without treating a timeout as an empty source.
 *
 * @param {string} docId - Document ID
 * @returns {Promise<Uint8Array|null>} CRDT state or null
 */
async function loadCRDTStateFromIndexedDB(docId) {
  return new Promise((resolve, reject) => {
    // Create a temporary Y.Doc to load from IndexedDB
    const doc = new Y.Doc();
    const persistence = new IndexeddbPersistence(`orbital-${docId}`, doc);

    // An empty database emits synced normally. A timeout means it is unread.
    const timeout = setTimeout(() => {
      persistence.destroy();
      doc.destroy();
      reject(new Error('Local persistence timed out'));
    }, 5000);

    persistence.once("synced", () => {
      clearTimeout(timeout);

      // Get full state
      const state = Y.encodeStateAsUpdate(doc);
      persistence.destroy();
      doc.destroy();
      resolve(state.length > 2 ? state : null);
    });
  });
}

/**
 * Phase 5c (H1): read a doc's raw Yjs state from the Tauri vault.
 * Reads both the user-visible vault and the shared-document cache, preserving
 * either readable source if the other fails. A browser has neither source.
 */
async function loadCRDTStateFromTauri(docId) {
  const sources = [], warnings = [];
  if (
    typeof window === "undefined" ||
    !window.__TAURI_INTERNALS__ ||
    typeof window.__TAURI_INTERNALS__.invoke !== "function"
  ) {
    return { sources, warnings };
  }
  for (const [kind, command] of [['main', 'doc_read'], ['cache', 'cache_read']]) {
    try {
      const bytes = await readVaultFile(kind, docId);
      if (bytes.length) sources.push(bytes);
    } catch (error) {
      warnings.push(t('vault.export.warn.nativeFailed', { command, error: String(error) }));
    }
  }
  return { sources, warnings };
}

/**
 * Convert CRDT bytes to a human-readable tree structure
 * @param {Uint8Array} crdtBytes - CRDT state
 * @returns {Object|null} Readable tree or null
 */
function crdtToReadableTree(crdtBytes) {
  try {
    const doc = new Y.Doc();
    Y.applyUpdate(doc, crdtBytes);

    // Either map schema (engine/sync/map-format.js): the nodes and where each shows.
    const metadata = doc.getMap("metadata");
    const { rootId, flat, children } = readMap(doc);

    if (!rootId || flat.size === 0) {
      doc.destroy();
      return null;
    }

    // Build a map of the nodes the map shows
    const nodeMap = new Map();
    flat.forEach((flatNode, id) => {
      if (!children.has(id)) return; // v2: deleted with its parent
      // Extract all meaningful properties (excluding internal CRDT stuff)
      // Preserve all node properties, including document links, execution
      // fields and future additions. Only the flat hierarchy is rebuilt.
      const { childIds: _childIds, parentId: _parentId, order: _order, ...properties } = flatNode;
      const node = { ...properties, id: flatNode.id || id, name: flatNode.name || '' };

      node.children = [];
      nodeMap.set(id, { node, childIds: children.get(id) || [] });
    });

    // yxml-v1 docs: the note body lives in a Y.XmlFragment named
    // `notes-<nodeId>` and the flat `notes` string is blank. Serialize the
    // fragment to markdown so the readable tree matches what the app shows.
    // `doc.share.has` avoids materializing empty fragments for nodes without
    // notes. The flat string stays as fallback when serialization fails.
    const canSerialize = isNotesSerializerLoaded();
    if (metadata.get("notesFormat") === "yxml-v1" && canSerialize) {
      nodeMap.forEach(({ node }, id) => {
        const name = "notes-" + id;
        if (!doc.share.has(name)) return;
        const fragment = doc.getXmlFragment(name);
        if (fragment.length === 0) return;
        try {
          const md = fragmentToMarkdown(fragment);
          if (md) node.notes = md;
        } catch (err) {
          console.warn(`[DataExport] Failed to serialize notes fragment for ${id}:`, err);
        }
      });
    }

    // Build the tree by connecting children
    nodeMap.forEach(({ node, childIds }) => {
      node.children = childIds
        .map((cid) => nodeMap.get(cid)?.node)
        .filter(Boolean);
    });

    const rootNode = nodeMap.get(rootId)?.node;

    // Extract document-level notes from Y.Array. Same dual-read as nodes:
    // in yxml-v1 mode each note's body is in `docnote-<noteId>` and the
    // array entry's `content` string is blank (or stale).
    const docNotesArray = doc.getArray("docNotes");
    // Deleted notes left out, as the app shows them (yjs-doc.js getDocNotes).
    const deletedNotes = doc.share.has("deletedDocNotes") ? doc.getMap("deletedDocNotes") : null;
    let docNotes = docNotesArray.length > 0
      ? docNotesArray.toArray().filter((note) => !note?.id || !deletedNotes?.has(note.id))
      : [];
    if (metadata.get("docNotesFormat") === "yxml-v1" && canSerialize) {
      docNotes = docNotes.map((note) => {
        if (!note || typeof note !== "object" || !note.id) return note;
        const name = "docnote-" + note.id;
        if (!doc.share.has(name)) return note;
        const fragment = doc.getXmlFragment(name);
        if (fragment.length === 0) return note;
        try {
          const md = fragmentToMarkdown(fragment);
          return md ? { ...note, content: md } : note;
        } catch (err) {
          console.warn(`[DataExport] Failed to serialize doc note fragment for ${note.id}:`, err);
          return note;
        }
      });
    }

    const routines=[...doc.getMap('routines').values()];
    doc.destroy();

    return { tree: rootNode || null, docNotes, routines };
  } catch (err) {
    console.error("[DataExport] Failed to convert CRDT to tree:", err);
    return null;
  }
}

/**
 * Convert a content tree (from export) back to app state format
 * Used when importing without CRDT state
 * @param {Object} contentTree - The content tree from export
 * @returns {Object} App state format
 */
function contentTreeToAppState(contentTree) {
  if (!contentTree) return null;

  // The content tree is already in the right format (nested with children)
  // Just wrap it in app state structure
  return {
    data: contentTree,
    ganttScale: "auto",
    ganttZoom: 1,
    focusId: contentTree.id,
    selectedId: contentTree.id,
    view: "dashboard",
    userSetProjectDates: false,
    hideCompleted: false,
  };
}

/**
 * Encrypt CRDT state for server sync using document's DEK
 * Uses the same format as websocket.js _encryptPayload:
 * - AES-GCM encryption
 * - IV (12 bytes) prepended to ciphertext
 * - Result base64 encoded
 *
 * @param {Uint8Array} crdtState - Raw CRDT state bytes
 * @param {Uint8Array} dekBytes - Document encryption key (raw bytes)
 * @param {string} docId - Document ID for AAD binding
 * @returns {Promise<string>} Base64-encoded encrypted payload
 */
async function encryptCRDTForSync(crdtState, dekBytes, docId) {
  // Import the raw DEK as a CryptoKey
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    dekBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  // Generate random IV (12 bytes for AES-GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt with AES-GCM (AAD binds ciphertext to this document)
  const aad = docId ? new TextEncoder().encode(docId) : undefined;
  const encryptParams = { name: "AES-GCM", iv: iv };
  if (aad) encryptParams.additionalData = aad;
  const encrypted = await crypto.subtle.encrypt(
    encryptParams,
    cryptoKey,
    crdtState
  );

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Return as base64
  return arrayBufferToBase64(combined);
}

/**
 * Sync imported documents to the server via batch import endpoint
 *
 * @param {Array<{doc_id: string, crdt_state: string}>} documents - Encrypted document states
 * @returns {Promise<{success: boolean, synced: number, errors: string[]}>}
 */
async function syncImportedDocumentsToServer(documents, onActivity) {
  if (!documents || documents.length === 0) {
    return { success: true, synced: 0, errors: [] };
  }

  try {
    console.log(
      `[DataImport] Syncing ${documents.length} documents to server...`
    );

    const response = await uploadWithProgress("/api/crdt/batch-import", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "include",
      body: JSON.stringify({ documents }),
    }, onActivity);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[DataImport] Server sync failed: ${response.status}`, errorText);
      return {
        success: false,
        synced: 0,
        errors: [t('vault.import.serverSyncFailed', { status: response.status, details: errorText })],
      };
    }

    const result = await response.json();
    for (const doc of documents) {
      if (result.errors?.some(error => error.includes(doc.doc_id) && isQuotaError(error))) recordQuotaRejection(doc.doc_id);
    }
    console.log(`[DataImport] Server sync complete:`, result);

    return {
      success: !(result.errors?.length),
      synced: result.synced ?? documents.length,
      errors: result.errors || [],
    };
  } catch (err) {
    console.error("[DataImport] Server sync error:", err);
    return {
      success: false,
      synced: 0,
      errors: [t('vault.import.networkError', { error: err.message })],
    };
  }
}

/** Persist a newly allocated recovery copy before the source can be removed.
 * The caller must retain its source and recovery claim if this throws. */
export async function persistRecoveryCopy(docId, tree, dekBytes, crdtState = null) {
  const { YjsDocManager } = await import('../sync/yjs-doc.js');
  const manager = new YjsDocManager(docId);
  try {
    if (crdtState) {
      manager.applyUpdate(crdtState);
      const rootId = manager.metadata.get('rootId');
      if (!rootId || !manager.hasNode(rootId)) throw new Error('Recovered document has no readable root');
      manager.updateNode(rootId, { name: tree.name });
    } else manager.fromTree(tree);
    const bytes = manager.getFullState();
    const mark = changeMark(docId);
    await writeCRDTStateToIndexedDB(docId, bytes);
    await writeCRDTStateToTauri(docId, bytes);
    const result = await syncImportedDocumentsToServer([{ doc_id: docId,
      crdt_state: await encryptCRDTForSync(bytes, dekBytes, docId) }]);
    if (!result.success) throw new Error(t('vault.import.recoveryNotSynced'));
    markConfirmedIfUnchanged(docId, mark);
  } finally { manager.destroy(); }
}

/**
 * Import data from an export file
 *
 * @param {Object} exportData - The exported data object
 * @param {Object} options
 * @param {Function} options.onProgress - Progress callback (current, total, name)
 * @param {boolean} options.mergeWithExisting - If true, don't delete existing data
 * @returns {Promise<{imported: number, skipped: number, errors: string[], syncErrors: string[]}>}
 */
export async function importAllData(exportData, options = {}) {
  return runOperation(t('vault.import.maps'), progress => importAllDataImpl(exportData, { ...options, onActivity: progress.update }),
    result => [t(result.errors.length || result.syncErrors.length ? 'vault.import.finishedWithWarnings' : 'vault.import.done'),
      !!(result.errors.length || result.syncErrors.length)]);
}

async function importAllDataImpl(exportData, options) {
  const { onProgress, mergeWithExisting = true } = options;

  if (!VaultStorage.getIndex()) throw new Error("Vault not loaded");

  if (!exportData || !exportData.version) {
    throw new Error(t('vault.import.invalidFormat'));
  }

  if (exportData.version > 2) {
    throw new Error(t('vault.import.unsupportedVersion', { version: String(exportData.version) }));
  }

  // Validate structure before beginning import to prevent partial state on malformed files
  if (!Array.isArray(exportData.documents)) {
    throw new Error(t('vault.import.invalidDocuments'));
  }
  for (let i = 0; i < exportData.documents.length; i++) {
    const doc = exportData.documents[i];
    if (!doc || typeof doc.id !== "string" || typeof doc.name !== "string") {
      throw new Error(t('vault.import.invalidEntry', { index: String(i) }));
    }
  }

  const documentIds = new Set();
  for (const doc of exportData.documents) {
    if (documentIds.has(doc.id)) throw new Error(t('vault.import.duplicateIds'));
    documentIds.add(doc.id);
  }
  if (exportData.folders && !Array.isArray(exportData.folders)) throw new Error(t('vault.import.invalidFolders'));
  const folderParents = new Map((exportData.folders || []).map(f => [f.id, f.parentId]));
  for (const id of folderParents.keys()) {
    const visited = new Set();
    let cursor = id;
    while (cursor && folderParents.has(cursor)) {
      if (visited.has(cursor)) throw new Error(t('vault.import.cyclicFolders'));
      visited.add(cursor); cursor = folderParents.get(cursor);
    }
  }

  const index = VaultStorage.getIndex();
  if (!index) {
    throw new Error("Vault not loaded");
  }

  const results = {
    imported: 0,
    skipped: 0,
    errors: [],
    syncErrors: [],
    newDocIds: [],
  };

  // Collect documents for server sync (encrypted CRDT states)
  const documentsToSync = [];
  const prepared = [];
  const docIdMap = new Map();
  const archive = options.attachmentArchive || exportData._attachmentArchive;
  for (const warning of archive?.manifest.warnings || []) results.errors.push(t('vault.import.sourceWarning', { warning: typeof warning === 'string' ? warning : warning.error }));

  // First, import folders (create any missing ones)
  // Build a lookup: "parentId|name" -> folder for matching by name within parent
  const existingFolderIds = new Set(index.folders.map(f => f.id));
  const folderIdMap = new Map(); // storage:sourceId -> destinationId
  const sourceFolders = new Map((exportData.folders || []).map(folder => [folder.id, folder]));
  const storageByFolder = new Map();
  // Older backups lack folder storage tags. Infer them from documents without
  // collapsing same-named local and online roots into a single folder.
  const includeStorage = (id, storage) => {
    const seen = new Set();
    while (sourceFolders.has(id) && !seen.has(id)) {
      seen.add(id);
      if (!storageByFolder.has(id)) storageByFolder.set(id, new Set());
      storageByFolder.get(id).add(storage);
      id = sourceFolders.get(id).parentId;
    }
  };
  for (const folder of sourceFolders.values()) if (folder.storage) includeStorage(folder.id, folder.storage);
  for (const doc of exportData.documents) includeStorage(doc.folderId, doc.storage || 'online');
  const restoreFolder = async (id, storage) => {
    const mapKey = `${storage}:${id}`;
    if (folderIdMap.has(mapKey)) return folderIdMap.get(mapKey);
    const source = sourceFolders.get(id);
    if (!source) return null;
    if (!['local', 'online'].includes(storage)) throw new Error('Unsupported vault storage type');
    let parentId = source.parentId ? await restoreFolder(source.parentId, storage) : null;
    const currentFolders = VaultStorage.getIndex().folders;
    // Imported roots use the matching storage root. This does not rename the
    // user's existing root or create a second accidental root.
    const existing = currentFolders.find(folder => (folder._storage || 'online') === storage &&
      (source.parentId === null ? folder.parentId === null : folder.parentId === parentId && folder.name === source.name));
    let targetId = existing?.id;
    if (!targetId) {
      targetId = source.parentId === null
        ? await VaultStorage.ensureRootFolder(storage)
        : await VaultStorage.createFolder(source.name, parentId, { storage });
    }
    folderIdMap.set(mapKey, targetId);
    existingFolderIds.add(targetId);
    return targetId;
  };
  for (const folder of sourceFolders.values()) {
    for (const storage of storageByFolder.get(folder.id) || ['online']) {
      try { await restoreFolder(folder.id, storage); }
      catch (error) {
        results.errors.push(t('vault.import.folderFailed', { name: folder.name, error: error.message }));
      }
    }
  }

  // Import documents
  const total = exportData.documents?.length || 0;
  let current = 0;

  for (const doc of exportData.documents || []) {
    options.onActivity?.({ label: t('vault.import.creating'), current, total, detail: doc.name });
    current++;
    if (onProgress) {
      onProgress(current, total, doc.name);
    }

    try {
      // Check if document already exists
      const existingMap = (index.docs || []).find((m) => m.docId === doc.id);
      if (existingMap && mergeWithExisting) {
        console.log(`[DataImport] Skipping existing document: ${doc.name}`);
        results.skipped++;
        docIdMap.set(doc.id, doc.id);
        continue;
      }

      // Determine folder ID (may have been remapped)
      let folderId = doc.folderId;
      if (folderId && folderIdMap.has(`${doc.storage || 'online'}:${folderId}`)) {
        folderId = folderIdMap.get(`${doc.storage || 'online'}:${folderId}`);
      } else if (folderId && !existingFolderIds.has(folderId)) {
        // Folder doesn't exist, use root
        folderId = VaultStorage.getIndex().folders.find(f => f.parentId === null && (f._storage || 'online') === (doc.storage || 'online'))?.id;
      }

      // Create initial state from CRDT data or content
      let initialState = null;
      let crdtBytes = null;

      if (doc.crdtState) {
        // Prefer CRDT state for full-fidelity restore
        crdtBytes = base64ToArrayBuffer(doc.crdtState);
        initialState = crdtBytesToAppState(crdtBytes);
        if (!initialState) throw new Error(t('vault.import.noRoot'));
      } else if (doc.content) {
        // Fallback to content tree if no CRDT state
        // Convert content tree to app state format
        initialState = contentTreeToAppState(doc.content);
        initialState.docNotes = doc.docNotes || [];
        initialState.routines = doc.routines || [];
        console.log(`[DataImport] Using content tree for: ${doc.name}`);
      }
      if (!initialState) throw new Error(t('vault.import.noContent'));

      // Create the map. Phase 5c (H1): honor the per-doc storage
      // tag so local docs are recreated as local (no DEK, no server
      // round-trip). Bundles from pre-Phase-5c versions don't have
      // this field; default to "online" for backward-compat with the
      // original export format.
      const requestedStorage = doc.storage || "online";
      const { docId: newDocId } = await VaultStorage.createMap(
        doc.name,
        folderId,
        initialState,
        { storage: requestedStorage, makeCurrent: options.keepCurrentDocument !== true },
      );

      docIdMap.set(doc.id, newDocId);
      prepared.push({ doc, newDocId, crdtBytes, requestedStorage });
      results.imported++;
      results.newDocIds.push(newDocId);
      console.log(`[DataImport] Imported: ${doc.name} -> ${newDocId}`);
    } catch (err) {
      console.error(`[DataImport] Failed to import ${doc.name}:`, err);
      results.errors.push(`${doc.name}: ${err.message}`);
    }
  }

  console.log(
    `[DataImport] Local import complete: ${results.imported} imported, ${results.skipped} skipped, ${results.errors.length} errors`
  );

  // Allocate all IDs before remapping so forward links and cycles resolve to
  // the restored maps, including execution-list references in document notes.
  let restoredCount = 0;
  for (const item of prepared) {
    const { doc, newDocId, requestedStorage } = item;
    options.onActivity?.({ label: t('vault.import.restoring'), current: restoredCount++, total: prepared.length, detail: doc.name });
    try {
      if (doc.meta) await VaultStorage.setDocEntry({ docId: newDocId, meta: doc.meta });
      const referenceData = [doc, item.crdtBytes ? [...backupAttachmentReferences(item.crdtBytes).keys()].map(ref => `attachment:${ref}`) : []];
      const restored = archive ? await restoreMapAttachments(archive, doc.id, newDocId, referenceData, { onProgress: options.onActivity })
        : { replacements: new Map(), warnings: [] };
      const attachmentNote = archive && restoredAttachmentNote(archive, doc.id, referenceData, restored.replacements);
      results.errors.push(...restored.warnings.map(warning => `${doc.name}: ${warning}`));
      if (!item.crdtBytes) {
        if (restored.replacements.size) {
          const initial = contentTreeToAppState(remapAttachmentData(doc.content, restored.replacements));
          initial.docNotes = remapAttachmentData(doc.docNotes || [], restored.replacements);
          initial.routines = remapAttachmentData(doc.routines || [], restored.replacements);
          if (attachmentNote) initial.docNotes.push(attachmentNote);
          VaultStorage.setPendingInitialState(newDocId, initial);
        }
        continue;
      }
      options.onActivity?.({ label: t('vault.import.relinking'), detail: doc.name });
      const crdtBytes = remapBackupLinks(item.crdtBytes, docIdMap, restored.replacements, attachmentNote ? [attachmentNote] : []);
      await writeCRDTStateToIndexedDB(newDocId, crdtBytes);
      await writeCRDTStateToTauri(newDocId, crdtBytes);
      if (requestedStorage === 'online') {
        const dekBytes = await VaultStorage.getDocDEK(newDocId);
        try {
          documentsToSync.push({ doc_id: newDocId,
            crdt_state: await encryptCRDTForSync(crdtBytes, dekBytes, newDocId) });
        } finally { dekBytes.fill(0); }
      }
    } catch (error) {
      results.errors.push(t('vault.import.restoreIncomplete', { name: doc.name, error: error.message }));
    }
  }

  // Sync imported documents to server
  // This ensures documents persist across logout/login (IndexedDB is cleared on logout)
  if (documentsToSync.length > 0) {
    const marks = new Map(documentsToSync.map((document) => [document.doc_id, changeMark(document.doc_id)]));
    const syncResult = await syncImportedDocumentsToServer(documentsToSync, options.onActivity);
    // The server has what was imported: confirmed, unless changed since.
    if (syncResult.success) for (const [docId, mark] of marks) markConfirmedIfUnchanged(docId, mark);
    if (!syncResult.success) {
      results.syncErrors.push(...syncResult.errors);
      console.warn(
        `[DataImport] Server sync failed for ${documentsToSync.length} documents:`,
        syncResult.errors
      );
    } else {
      console.log(
        `[DataImport] Server sync complete: ${syncResult.synced} documents synced`
      );
    }
  }

  console.log(
    `[DataImport] Complete: ${results.imported} imported, ${results.skipped} skipped, ${results.errors.length} errors, ${results.syncErrors.length} sync errors`
  );
  return results;
}

/**
 * Write CRDT state to IndexedDB for a document
 * @param {string} docId - Document ID
 * @param {Uint8Array} state - CRDT state
 */
/**
 * Phase 5c (H1): write a doc's raw CRDT state to the Tauri vault so
 * the imported full Yjs history is preserved across the first open.
 * No-op on the web (where the IndexedDB path covers it).
 */
async function writeCRDTStateToTauri(docId, state) {
  if (
    typeof window === "undefined" ||
    !window.__TAURI_INTERNALS__ ||
    typeof window.__TAURI_INTERNALS__.invoke !== "function"
  ) {
    return;
  }
  try {
    // doc_write places the file at <vault>/<docId>.orbital — the
    // same location FilePersistence reads from on doc open. Both
    // local and online owner docs use this path; shared cached docs
    // would use cache_write, but those aren't in the owner-doc
    // import loop.
    await writeVaultFile("main", docId, state);
  } catch (err) {
    console.warn(`[DataImport] Tauri doc_write failed for ${docId}:`, err);
    throw err;
  }
}

async function writeCRDTStateToIndexedDB(docId, state) {
  const doc = new Y.Doc();
  const persistence = new IndexeddbPersistence(`orbital-${docId}`, doc);
  let timeout;
  try {
    await Promise.race([persistence.whenSynced, new Promise((_, reject) => {
      timeout = setTimeout(() => reject(new Error('Timeout opening backup storage')), 10000);
    })]);
    Y.applyUpdate(doc, state);
    // Wait for an actual transaction commit, not an arbitrary delay. Append
    // the merged state; never discard any existing local update.
    await new Promise((resolve, reject) => {
      const transaction = persistence.db.transaction(['updates'], 'readwrite');
      transaction.objectStore('updates').add(Y.encodeStateAsUpdate(doc));
      transaction.oncomplete = resolve;
      transaction.onerror = transaction.onabort = () => reject(transaction.error || new Error('Backup storage failed'));
    });
  } finally {
    clearTimeout(timeout);
    await persistence.destroy();
    doc.destroy();
  }
}

/**
 * Convert CRDT bytes to app state (tree structure)
 * Used for initial state when creating imported maps
 * @param {Uint8Array} crdtBytes - CRDT state
 * @returns {Object|null} App state or null
 */
function crdtBytesToAppState(crdtBytes) {
  try {
    const doc = new Y.Doc();
    Y.applyUpdate(doc, crdtBytes);

    const metadata = doc.getMap("metadata");
    // Either map schema (engine/sync/map-format.js), as the map shows it.
    const rootNode = readTree(doc, (flatNode) => {
      const { childIds: _childIds, parentId: _parentId, order: _order, ...properties } = flatNode;
      return { ...properties, id: flatNode.id, name: flatNode.name || '' };
    });
    const ganttScale = metadata.get("ganttScale");
    const ganttZoom = metadata.get("ganttZoom");
    doc.destroy();

    if (!rootNode) return null;

    // Build app state
    return {
      data: rootNode,
      ganttScale: ganttScale || "auto",
      ganttZoom: ganttZoom || 1,
      focusId: rootNode.id,
      selectedId: rootNode.id,
      view: "dashboard",
      userSetProjectDates: false,
      hideCompleted: false,
    };
  } catch (err) {
    console.error("[DataImport] Failed to convert CRDT to app state:", err);
    return null;
  }
}

/**
 * The documents of an export as a map archive lists them (interfaces/classic/ui/export-flow.js
 * downloadExport): each with the attachments its content refers to.
 * @param {Object} exportData
 * @param {boolean} includeAttachments
 */
export function archiveDocuments(exportData, includeAttachments) {
  return exportData.documents.map(doc => {
    let refs = [];
    if (doc.crdtState && includeAttachments) {
      try { refs = [...backupAttachmentReferences(base64ToArrayBuffer(doc.crdtState)).keys()]; }
      catch { /* Damaged source is retained; exportAllData already reports it. */ }
    }
    return { id: doc.id, name: doc.name, data: [doc, refs.map(ref => `attachment:${ref}`)],
      listAll: (doc.storage || 'online') === 'online' };
  });
}

/**
 * Read an export file and parse it
 * @param {File} file - File object from input
 * @returns {Promise<Object>} Parsed export data
 */
export async function readExportFile(file) {
  return runOperation(t('vault.import.readTitle'), progress => readExportFileImpl(file, progress.update), () => [t('vault.import.fileChecked')]);
}

async function readExportFileImpl(file, onActivity) {
  if (/\.zip$/i.test(file.name)) {
    const archive = await readMapArchive(file, onActivity);
    if (!archive || /\.md$/i.test(archive.manifest.mapFile)) return { _markdownArchive: archive, _markdownFile: file };
    if (!/\.json$/i.test(archive.manifest.mapFile)) throw new Error(t('vault.import.presentationArchive'));
    let data = JSON.parse(new TextDecoder().decode(archive.mapBytes));
    if (data.id && !data.data && !data.documents) data = { version: 1, name: data.name, data, branchOnly: true };
    return attachArchiveContext(data, archive);
  }
  if (file.size > 50 * 1024 * 1024) throw new Error(t('vault.import.jsonTooLarge'));
  onActivity({ label: t('vault.import.reading') });
  return JSON.parse(await file.text());
}


// ============================================
// Utility Functions
// ============================================

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunks = [];
  for (let i = 0; i < bytes.length; i += 8192) {
    chunks.push(String.fromCharCode.apply(null, bytes.subarray(i, i + 8192)));
  }
  return btoa(chunks.join(''));
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
